import type { CSSProperties, ReactNode } from 'react';
import LanguageFlag from './LanguageFlag';
import type { PCBarState, PCBarVisitor } from './PCBarView';

// "Ingrijpen" — opens from a PC bar (design/Intervene.dc.html). Presentational:
// every action is a handler PCTile / FakePCTile already owns, every gate is a
// boolean the tile already derived from utils/permissions.ts.
//
// Permission rule from the mockup: an action the account may not use stays
// VISIBLE but disabled, with the reason in place of its subtitle, so everyone
// learns the option exists and who to ask.
//
// Tiering rule (RealityKnowledge rules/operator-ui-show-by-exception): the
// operator sees verdicts and the few things that change their next action;
// raw telemetry (IP, versions, hardware id) is marked `admin` and only renders
// for an admin account. The sheet never decides what is admin-only itself — the
// tile passes the flag with the value.
//
// Layout: one wide modal, two columns on a wide screen. Left is what an
// operator reaches for, right is state and admin detail. The body scrolls; the
// header and the "Meld dit als probleem" footer stay put.

export interface InterveneAction {
  onClick: () => void;
  /** Not allowed, or not applicable right now. */
  disabled?: boolean;
  /** Dutch reason shown instead of the subtitle while disabled. */
  reason?: string | null;
}

export type InterveneTone = 'ok' | 'warn' | 'bad' | 'muted';

/** One chip on the header fact row. */
export interface InterveneFact {
  key: string;
  label: string;
  tone?: InterveneTone;
  title?: string;
  /** Tappable facts open a detail sheet (BRIL → HeadsetSheet). */
  onClick?: () => void;
  /** Raw telemetry: rendered for admins only. */
  admin?: boolean;
}

/** One installed release, launched through the tile's own experience-click path. */
export interface InterveneLaunchItem {
  key: string;
  name: string;
  /** Only set when the account may see release details. */
  version?: string | null;
  phase?: string | null;
  /** Play language when the release has exactly one. */
  language?: string | null;
  color?: { border: string; bg: string };
  action: InterveneAction;
}

export interface InterveneLaunchView {
  items: InterveneLaunchItem[];
  /** Quiet line under the label: why starting is blocked right now. */
  note?: string | null;
  /** Pre-launch checklist, for the roles that require one. */
  confirm?: (InterveneAction & { label: string }) | null;
  /** "Markeer als testrun" — the tile's skip-billing toggle. Omit to hide. */
  testRun?: { checked: boolean; onChange: (v: boolean) => void } | null;
  /** Only while a launch is in flight: the tile's back button, which stops
   *  waiting on this launch so the PC can be used for something else. */
  onCancel?: InterveneAction | null;
}

export interface InterveneVolume {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  reason?: string | null;
}

export interface InterveneSyncView {
  tone: 'busy' | 'paused';
  title: string;
  detail?: string | null;
  /** 0..100 from the agent's sync block. */
  pct?: number | null;
  pause?: InterveneAction | null;
  resume?: InterveneAction | null;
}

export interface InterveneDiagView {
  label: string;
  tone: InterveneTone;
  detail?: string | null;
  failures?: { id: string; message: string }[];
  onRun?: InterveneAction | null;
  onReport?: InterveneAction | null;
  onOverride?: InterveneAction | null;
  /** "Controles overslaan" while a run of checks is in progress. Distinct from
   *  onOverride: abort stops the checks, override ignores a failed verdict. */
  onAbort?: InterveneAction | null;
}

/** "Uit de line-up halen" / "Weer in dienst". The sheet only renders what the
 *  tile computed: whether the PC is currently out, the one-line why, and the
 *  handler that flips it. */
export interface InterveneOutOfServiceView {
  active: boolean;
  /** While active: "Reparatie · wacht op kabel · Dirk, 14:12". */
  detail?: string | null;
  action: InterveneAction;
}

export interface InterveneSoftwareView {
  items: { key: string; name: string; version?: string | null; phase?: string | null }[];
  updateLabel: string;
  updateNote?: string | null;
  onUpdate: InterveneAction;
  force: boolean;
  onForceChange: (v: boolean) => void;
}

export interface InterveneSheetProps {
  pcName: string;
  /** Drives the header dot colour — same four-colour vocabulary as the bar. */
  state: PCBarState;
  /** The bar's own state line ("Vrij", "Laden…", the problem message). */
  stateText?: string | null;
  /** The visitor this run belongs to (null for a free / unassigned PC). */
  visitor?: PCBarVisitor | null;
  /** What is running, when there is no visitor row to carry the name. */
  experienceName?: string | null;
  /** Something is actually running on this PC (walk-in runs have no visitor). */
  runActive?: boolean;
  /** "Gestart 14:12 · scene 3 van 5 · nog ~9 min". */
  metaLine?: string | null;
  /** Header fact row; entries marked `admin` need isAdmin. */
  facts?: InterveneFact[];
  isAdmin?: boolean;
  /** Deze bezoeker. */
  onRestart: InterveneAction;
  onMove: InterveneAction;
  onRequeue: InterveneAction;
  onStop: InterveneAction;
  /** Scene controls on the RUNNING experience (Permission.CONTROL_EXPERIENCE_LEVELS).
   *  Null for an account without the grant, so the pair disappears rather than
   *  sitting greyed in the section an operator uses most. */
  onNextScene?: InterveneAction | null;
  onPickScene?: (InterveneAction & { busy?: boolean }) | null;
  /** Handmatig starten — omit to hide the section entirely. */
  launch?: InterveneLaunchView | null;
  /** Deze computer. */
  onRestartPC: InterveneAction;
  onRebootHmd: InterveneAction;
  onAutoRepair: InterveneAction;
  onInvalidateRun: InterveneAction;
  onSendMessage?: InterveneAction | null;
  /** Offered exactly where the tile offers it: wake while off, shut down while on. */
  onWake?: InterveneAction | null;
  onShutdown?: InterveneAction | null;
  /** Take this PC out of the room lineup, or put it back. */
  outOfService?: InterveneOutOfServiceView | null;
  volume?: InterveneVolume | null;
  /** Remote en service — the tile's red-menu tail. The tile passes null for an
   *  account holding none of these, which is how the square tile hid its whole
   *  red button. Individually denied ones still render disabled with a reason. */
  onStartParsec?: InterveneAction | null;
  onRestartParsec?: InterveneAction | null;
  onRestartAgent?: InterveneAction | null;
  onCaptureLogs?: InterveneAction | null;
  /** Synchronisatie — only passed when there is something to say. */
  sync?: InterveneSyncView | null;
  /** Diagnose / Software — admin only. */
  diag?: InterveneDiagView | null;
  software?: InterveneSoftwareView | null;
  /** Meld dit als probleem. */
  onReportIssue: InterveneAction;
  onClose: () => void;
  errorText?: string | null;
}

type Tone = 'blue' | 'neutral' | 'danger' | 'amber';

function Row({
  icon,
  title,
  subtitle,
  tone,
  action,
  compact = false,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  tone: Tone;
  action: InterveneAction;
  compact?: boolean;
}) {
  const off = !!action.disabled;
  const cls = [
    'isheet-row',
    `isheet-row--${tone}`,
    compact ? 'isheet-row--compact' : '',
    off ? 'isheet-row--off' : '',
  ].filter(Boolean).join(' ');
  const sub = off ? (action.reason || 'Niet beschikbaar') : subtitle;
  return (
    <button type="button" className={cls} onClick={action.onClick} disabled={off} title={sub}>
      <span className="isheet-row-icon" aria-hidden="true">{icon}</span>
      <span className="isheet-row-text">
        <span className="isheet-row-title">{title}</span>
        {sub ? <span className="isheet-row-sub">{sub}</span> : null}
      </span>
    </button>
  );
}

/** Small button for the state/admin sections (Run checks, Rapport, Update). */
function MiniButton({ action, label }: { action: InterveneAction; label: string }) {
  const off = !!action.disabled;
  return (
    <button
      type="button"
      className={`isheet-mini${off ? ' isheet-mini--off' : ''}`}
      onClick={action.onClick}
      disabled={off}
      title={off ? (action.reason || 'Niet beschikbaar') : undefined}
    >
      {label}
    </button>
  );
}

const svg = (children: ReactNode, size = 19) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {children}
  </svg>
);

export default function InterveneSheet({
  pcName,
  state,
  stateText,
  visitor,
  experienceName,
  runActive = false,
  metaLine,
  facts,
  isAdmin = false,
  onRestart,
  onMove,
  onRequeue,
  onStop,
  onNextScene,
  onPickScene,
  launch,
  onRestartPC,
  onRebootHmd,
  onAutoRepair,
  onInvalidateRun,
  outOfService,
  onSendMessage,
  onWake,
  onShutdown,
  volume,
  onStartParsec,
  onRestartParsec,
  onRestartAgent,
  onCaptureLogs,
  sync,
  diag,
  software,
  onReportIssue,
  onClose,
  errorText,
}: InterveneSheetProps) {
  const experience = visitor?.experienceName ?? experienceName ?? null;
  const visibleFacts = (facts ?? []).filter((f) => isAdmin || !f.admin);
  // The red-menu tail only appears for an account that holds at least one of
  // these, mirroring how the square tile hid its red button entirely otherwise.
  const serviceActions = !!(onStartParsec || onRestartParsec || onRestartAgent || onCaptureLogs);

  // --- Deze bezoeker. Without a queue visitor this used to be four rows all
  // repeating the same "geen bezoeker" reason, which reads as broken. One quiet
  // line instead — plus Stop when something IS running here without a visitor
  // (a manual or walk-in run), because that action is still real.
  //
  // Scene controls sit ABOVE Stop in both branches: skipping a broken scene is
  // the gentler fix and should be reached before the destructive one.
  const sceneRows =
    onNextScene || onPickScene ? (
      <div className="isheet-pair">
        {onNextScene ? (
          <Row
            compact
            tone="neutral"
            action={onNextScene}
            title="Volgende scene"
            icon={svg(<><path d="M5 5l9 7-9 7z" /><path d="M19 5v14" /></>, 18)}
          />
        ) : null}
        {onPickScene ? (
          <Row
            compact
            tone="neutral"
            action={onPickScene}
            title={onPickScene.busy ? 'Scenes laden…' : 'Kies een scene'}
            icon={svg(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /><path d="M9 10v9" /></>, 18)}
          />
        ) : null}
      </div>
    ) : null;
  const visitorSection = (
    <section className="isheet-section" key="visitor">
      <div className="isheet-label">DEZE BEZOEKER</div>
      {visitor ? (
        <>
          <Row
            tone="blue"
            action={onRestart}
            title="Opnieuw starten"
            subtitle="Zelfde ervaring en taal, zelfde PC"
            icon={svg(<><path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 5v6h-6" /></>)}
          />
          <Row
            tone="blue"
            action={onMove}
            title="Verplaats naar andere PC"
            subtitle="Stopt hier, jij kiest de volgende PC"
            icon={svg(<><path d="M4 8h11" /><path d="M11 4l4 4-4 4" /><path d="M20 16H9" /><path d="M13 12l-4 4 4 4" /></>)}
          />
          <Row
            tone="neutral"
            action={onRequeue}
            title="Terug in wachtrij"
            subtitle="Komt terug op de eigen plek, niet achteraan"
            icon={svg(<><path d="M3 12h13" /><path d="M9 6l-6 6 6 6" /><path d="M21 5v14" /></>)}
          />
          {runActive ? sceneRows : null}
          <Row
            tone="danger"
            action={onStop}
            title="Stop de ervaring"
            subtitle="Bezoeker is klaar of stopt ermee"
            icon={svg(<rect x="6" y="6" width="12" height="12" rx="1.5" />)}
          />
        </>
      ) : runActive ? (
        <>
          <div className="isheet-quiet">Geen bezoeker uit de wachtrij op deze PC</div>
          {sceneRows}
          <Row
            tone="danger"
            action={onStop}
            title="Stop de ervaring"
            subtitle="Stopt wat hier nu draait"
            icon={svg(<rect x="6" y="6" width="12" height="12" rx="1.5" />)}
          />
        </>
      ) : (
        <div className="isheet-quiet">Geen bezoeker op deze PC</div>
      )}
    </section>
  );

  // --- Handmatig starten. The queue is not the only way a run begins: a test
  // run or a staff run needs the launch grid the square tile has.
  const launchSection = launch ? (
    <section className="isheet-section" key="launch">
      <div className="isheet-label">HANDMATIG STARTEN</div>
      <div className="isheet-quiet">Buiten de wachtrij om, voor een test of personeel.</div>
      {launch.onCancel ? (
        <Row
          tone="danger"
          action={launch.onCancel}
          title="Start afbreken"
          subtitle="Stopt het wachten op deze start"
          icon={svg(<><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></>)}
        />
      ) : null}
      {launch.note ? <div className="isheet-warn">{launch.note}</div> : null}
      {launch.testRun ? (
        // Set BEFORE the launch, like the tile's red-menu checkbox: the run is
        // created non-billable so a test never lands in the day's takings.
        <label className="isheet-check">
          <input
            type="checkbox"
            checked={launch.testRun.checked}
            onChange={(e) => launch.testRun!.onChange(e.target.checked)}
          />
          <span>Markeer als testrun (niet factureren)</span>
        </label>
      ) : null}
      {launch.confirm ? (
        <Row
          tone="amber"
          action={launch.confirm}
          title={launch.confirm.label}
          subtitle="Bevestig de checklist voordat je start"
          icon={svg(<><path d="M4 12l5 5L20 6" /></>)}
        />
      ) : null}
      {launch.items.length === 0 ? (
        <div className="isheet-quiet">Geen ervaringen op deze PC</div>
      ) : (
        <div className="isheet-launch-grid">
          {launch.items.map((item) => {
            const off = !!item.action.disabled;
            const style = item.color
              ? ({ '--isheet-launch-color': item.color.border, '--isheet-launch-bg': item.color.bg } as CSSProperties)
              : undefined;
            return (
              <button
                key={item.key}
                type="button"
                className={`isheet-launch${off ? ' isheet-launch--off' : ''}`}
                style={style}
                onClick={item.action.onClick}
                disabled={off}
                title={off ? (item.action.reason || 'Niet beschikbaar') : `Start ${item.name} op deze PC`}
              >
                <span className="isheet-launch-name">{item.name}</span>
                {item.language ? <LanguageFlag code={item.language} className="qbar-flag" /> : null}
                {item.version ? <span className="isheet-launch-version">v{item.version}</span> : null}
                {item.phase === 'beta' || item.phase === 'alpha' ? (
                  <span className={`isheet-launch-phase isheet-launch-phase--${item.phase}`}>
                    {item.phase.toUpperCase()}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </section>
  ) : null;

  // --- Deze computer: the machine, not the run.
  const computerSection = (
    <section className="isheet-section" key="computer">
      <div className="isheet-label">DEZE COMPUTER</div>
      <div className="isheet-pair">
        <Row
          compact
          tone="neutral"
          action={onRestartPC}
          title="Herstart PC"
          icon={svg(<><path d="M12 3v9" /><path d="M6.5 6.5a8 8 0 1 0 11 0" /></>, 18)}
        />
        <Row
          compact
          tone="neutral"
          action={onRebootHmd}
          title="Bril herstarten"
          icon={svg(<><path d="M4 14a8 8 0 0 1 8-8 8 8 0 0 1 8 8" /><path d="M12 6V3" /><path d="M9 18h6" /></>, 18)}
        />
      </div>
      <Row
        tone="amber"
        action={onAutoRepair}
        title="Automatisch herstel"
        subtitle="Beeld of streaming is stuk: de PC repareert zichzelf"
        icon={svg(<><path d="M14 5l5 5-9 9H5v-5z" /><path d="M12.5 6.5l5 5" /></>)}
      />
      {onSendMessage ? (
        <Row
          tone="blue"
          action={onSendMessage}
          title="Bericht naar bezoeker"
          subtitle="Speelt een ingesproken instructie af in de bril"
          icon={svg(<><path d="M4 5h16v11H8l-4 4z" /><path d="M8 10h8" /></>)}
        />
      ) : null}
      {volume ? (
        <div className={`isheet-volume${volume.disabled ? ' isheet-volume--off' : ''}`}>
          <div className="volume-control-v2">
            <span className="volume-label">VOLUME</span>
            <input
              type="range"
              min="0"
              max="100"
              value={volume.value}
              onChange={(e) => volume.onChange(parseInt(e.target.value, 10))}
              disabled={volume.disabled}
              aria-label="Volume"
              style={{ '--value': `${volume.value}%` } as CSSProperties}
            />
            <span className="isheet-volume-value">{volume.value}%</span>
          </div>
          {volume.disabled && volume.reason ? <div className="isheet-quiet">{volume.reason}</div> : null}
        </div>
      ) : null}
      {onWake ? (
        <Row
          tone="neutral"
          action={onWake}
          title="Zet computer aan"
          subtitle="Stuurt een wake-signaal naar deze PC"
          icon={svg(<><path d="M12 3v9" /><path d="M6.5 6.5a8 8 0 1 0 11 0" /></>)}
        />
      ) : null}
      {onShutdown ? (
        <Row
          tone="neutral"
          action={onShutdown}
          title="Zet computer uit"
          subtitle="Afsluiten, bijvoorbeeld aan het eind van de dag"
          icon={svg(<><path d="M12 3v9" /><path d="M6.5 6.5a8 8 0 1 0 11 0" /></>)}
        />
      ) : null}
      {outOfService ? (
        <Row
          tone={outOfService.active ? 'blue' : 'amber'}
          action={outOfService.action}
          title={outOfService.active ? 'Weer in dienst' : 'Uit de line-up halen'}
          subtitle={
            outOfService.active
              ? outOfService.detail || 'Deze PC staat buiten dienst'
              : 'Geen bezoekers meer op deze PC, tot je hem terugzet'
          }
          icon={svg(
            outOfService.active ? (
              <><path d="M5 12l5 5 9-9" /></>
            ) : (
              <><circle cx="12" cy="12" r="9" /><path d="M6 6l12 12" /></>
            ),
          )}
        />
      ) : null}
      <Row
        tone="neutral"
        action={onInvalidateRun}
        title="Run ongeldig maken"
        subtitle="Verwijdert deze run uit de administratie"
        icon={svg(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>)}
      />
      {serviceActions ? (
        <>
          {/* The square tile's red-menu tail. Nothing here touches the visitor's
              run: Parsec is the remote desktop into this PC, the agent is the
              service the operator app talks to, and the log bundle is for us. */}
          <div className="isheet-sublabel">REMOTE EN SERVICE</div>
          <div className="isheet-pair">
            {onStartParsec ? (
              <Row
                compact
                tone="neutral"
                action={onStartParsec}
                title="Parsec starten"
                icon={svg(<><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8" /></>, 18)}
              />
            ) : null}
            {onRestartParsec ? (
              <Row
                compact
                tone="neutral"
                action={onRestartParsec}
                title="Parsec herstarten"
                icon={svg(<><path d="M12 3v9" /><path d="M6.5 6.5a8 8 0 1 0 11 0" /></>, 18)}
              />
            ) : null}
          </div>
          <div className="isheet-pair">
            {onRestartAgent ? (
              <Row
                compact
                tone="neutral"
                action={onRestartAgent}
                title="Agent herstarten"
                icon={svg(<><path d="M12 3v9" /><path d="M6.5 6.5a8 8 0 1 0 11 0" /></>, 18)}
              />
            ) : null}
            {onCaptureLogs ? (
              <Row
                compact
                tone="neutral"
                action={onCaptureLogs}
                title="Logs vastleggen"
                icon={svg(<><path d="M5 4h10l4 4v12H5z" /><path d="M9 13h6" /><path d="M9 17h4" /></>, 18)}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );

  // --- Synchronisatie. Operator-visible, but only when the sync is worth a row.
  const syncSection = sync ? (
    <section className="isheet-section" key="sync">
      <div className="isheet-label">SYNCHRONISATIE</div>
      <div className={`isheet-state-card isheet-state-card--${sync.tone === 'paused' ? 'warn' : 'busy'}`}>
        <div className="isheet-state-title">{sync.title}</div>
        {sync.detail ? <div className="isheet-quiet">{sync.detail}</div> : null}
        {typeof sync.pct === 'number' ? (
          <div className="isheet-track-row">
            <div className="isheet-track">
              <div className="isheet-track-fill" style={{ width: `${Math.max(0, Math.min(100, sync.pct))}%` }} />
            </div>
            <span className="isheet-track-value">{Math.round(sync.pct)}%</span>
          </div>
        ) : null}
      </div>
      <div className="isheet-btn-row">
        {sync.pause ? <MiniButton action={sync.pause} label="Pauzeer sync" /> : null}
        {sync.resume ? <MiniButton action={sync.resume} label="Hervat sync" /> : null}
      </div>
    </section>
  ) : null;

  // --- Diagnose (admin). The verdict, the run, the report, the override.
  const diagSection = isAdmin && diag ? (
    <section className="isheet-section" key="diag">
      <div className="isheet-label">DIAGNOSE</div>
      <div className="isheet-chip-row">
        <span className={`isheet-chip isheet-chip--${diag.tone}`} title={diag.detail || undefined}>
          {diag.label}
        </span>
        {diag.detail ? <span className="isheet-quiet">{diag.detail}</span> : null}
      </div>
      {diag.failures && diag.failures.length > 0 ? (
        <ul className="isheet-list">
          {diag.failures.map((f) => (
            <li key={f.id} className="isheet-list-row">
              <span className="isheet-list-name">{f.id}</span>
              <span className="isheet-list-meta">{f.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="isheet-btn-row">
        {diag.onRun ? <MiniButton action={diag.onRun} label="Controles uitvoeren" /> : null}
        {diag.onAbort ? <MiniButton action={diag.onAbort} label="Controles overslaan" /> : null}
        {diag.onReport ? <MiniButton action={diag.onReport} label="Rapport" /> : null}
        {diag.onOverride ? <MiniButton action={diag.onOverride} label="Blokkade opheffen" /> : null}
      </div>
    </section>
  ) : null;

  // --- Software (admin). What is installed, and the agent update.
  const softwareSection = isAdmin && software ? (
    <section className="isheet-section" key="software">
      <div className="isheet-label">SOFTWARE</div>
      {software.items.length === 0 ? (
        <div className="isheet-quiet">Geen ervaringen op deze PC</div>
      ) : (
        <ul className="isheet-list">
          {software.items.map((item) => (
            <li key={item.key} className="isheet-list-row">
              <span className="isheet-list-name">{item.name}</span>
              <span className="isheet-list-meta">
                {item.version ? `v${item.version}` : '—'}
                {item.phase === 'beta' || item.phase === 'alpha' ? ` · ${item.phase}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="isheet-btn-row">
        <MiniButton action={software.onUpdate} label={software.updateLabel} />
        <label className="isheet-check">
          <input
            type="checkbox"
            checked={software.force}
            onChange={(e) => software.onForceChange(e.target.checked)}
          />
          <span>Force</span>
        </label>
      </div>
      {software.updateNote ? <div className="isheet-quiet">{software.updateNote}</div> : null}
    </section>
  ) : null;

  // Two columns: actions left, state and admin right. With nothing in the
  // state/admin column (a plain operator on a healthy PC) the machine section
  // moves over rather than leaving a hole next to a long scrolling column.
  const stateSections = [syncSection, diagSection, softwareSection].filter(Boolean);
  const computerRight = stateSections.length === 0;
  const leftColumn = [visitorSection, launchSection, computerRight ? null : computerSection].filter(Boolean);
  const rightColumn = computerRight ? [computerSection, ...stateSections] : stateSections;

  return (
    <div className="isheet-overlay" onClick={onClose} role="presentation">
      <div className="isheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Ingrijpen op ${pcName}`}>
        {/* Who and what, so the operator is sure they are acting on the right run */}
        <div className="isheet-head">
          <div className="isheet-head-top">
            <span className={`qbar-dot qbar-dot--${state}`} aria-hidden="true" />
            <span className="isheet-pc">{pcName}</span>
            {stateText ? <span className={`isheet-state isheet-state--${state}`}>{stateText}</span> : null}
            <span className="isheet-spacer" />
            <button type="button" className="isheet-close" onClick={onClose} aria-label="Sluiten">
              {svg(<path d="M6 6l12 12M18 6L6 18" />, 18)}
            </button>
          </div>
          {visitor ? (
            <div className="isheet-who">
              <span className="isheet-visitor">{visitor.name}</span>
              <LanguageFlag code={visitor.language} className="qbar-flag" />
              {visitor.party ? <span className="isheet-party">{visitor.party}</span> : null}
              {experience ? <span className="isheet-exp">· {experience}</span> : null}
            </div>
          ) : experience ? (
            <div className="isheet-who">
              <span className="isheet-exp">{experience}</span>
            </div>
          ) : null}
          {metaLine ? <div className="isheet-meta">{metaLine}</div> : null}
          {visibleFacts.length > 0 ? (
            <div className="isheet-facts">
              {visibleFacts.map((fact) => {
                const cls = [
                  'isheet-fact',
                  `isheet-fact--${fact.tone || 'muted'}`,
                  fact.admin ? 'isheet-fact--admin' : '',
                ].filter(Boolean).join(' ');
                return fact.onClick ? (
                  <button key={fact.key} type="button" className={cls} onClick={fact.onClick} title={fact.title}>
                    {fact.label}
                  </button>
                ) : (
                  <span key={fact.key} className={cls} title={fact.title}>{fact.label}</span>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="isheet-body">
          <div className="isheet-col">{leftColumn}</div>
          <div className="isheet-col">{rightColumn}</div>
        </div>

        <div className="isheet-foot">
          <button
            type="button"
            className={`isheet-report${onReportIssue.disabled ? ' isheet-row--off' : ''}`}
            onClick={onReportIssue.onClick}
            disabled={onReportIssue.disabled}
          >
            <span className="isheet-row-icon" aria-hidden="true">
              {svg(<path d="M4 21V4h10l1 2h5v9h-6l-1-2H6v8z" />, 17)}
            </span>
            <span className="isheet-report-title">Meld dit als probleem</span>
            <span className="isheet-spacer" />
            <span className="isheet-report-sub">
              {onReportIssue.disabled
                ? (onReportIssue.reason || 'Niet beschikbaar')
                : 'Legt logs en beeld vast voor later'}
            </span>
          </button>

          {errorText ? <div className="isheet-error">{errorText}</div> : null}
        </div>
      </div>
    </div>
  );
}
